import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { GalleryHorizontalEnd, Plus } from 'lucide-react';
import { useState } from 'react';
import type { UpsertBannerInput } from '@nokowazzi/types';
import { Badge, Button, Dialog, EmptyState, Spinner, toast } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { Can } from '../features/admin/Can';
import { adminBannersQuery } from '../features/admin/queries';
import type { AdminBannerRow } from '../features/admin/queries';
import { BannerForm } from '../features/banners/BannerForm';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';
import { imagePublicUrl } from '../lib/storage';
import { jsonLocaleText } from '../lib/json-i18n';

export const Route = createFileRoute('/_admin/banners')({
  component: BannersScreen,
});

const LINK_TYPE_LABEL: Record<string, string> = {
  none: '없음',
  product: '상품',
  request: '요청',
  url: 'URL',
};

function BannersScreen() {
  const queryClient = useQueryClient();
  const bannersQuery = useQuery(adminBannersQuery());
  /** 'new' = 신규 등록 폼, row = 수정 폼, null = 폼 닫힘 */
  const [editing, setEditing] = useState<AdminBannerRow | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminBannerRow | null>(null);

  const saveMutation = useMutation({
    mutationFn: (vars: { id: string | null; input: UpsertBannerInput }) =>
      vars.id != null
        ? apiFetch<unknown>(`/v1/admin/banners/${vars.id}`, { method: 'PUT', json: vars.input })
        : apiFetch<unknown>('/v1/admin/banners', { method: 'POST', json: vars.input }),
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      toast.show({
        type: 'success',
        message: vars.id != null ? '배너가 수정되었습니다' : '배너가 등록되었습니다',
      });
      setEditing(null);
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '저장에 실패했습니다',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch<unknown>(`/v1/admin/banners/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      toast.show({ type: 'success', message: '배너가 삭제되었습니다' });
      setDeleteTarget(null);
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '삭제에 실패했습니다',
      });
    },
  });

  const banners = bannersQuery.data ?? [];

  return (
    <>
      <PageHeader
        title="배너"
        actions={
          <Can perm="curation:write">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus className="size-4" aria-hidden="true" />}
              onClick={() => setEditing('new')}
            >
              배너 등록
            </Button>
          </Can>
        }
      />

      {editing != null && (
        <div className="mb-6">
          <BannerForm
            key={editing === 'new' ? 'new' : `${editing.id}-${editing.updated_at}`}
            initial={editing === 'new' ? undefined : editing}
            submitting={saveMutation.isPending}
            onSubmit={(input) =>
              saveMutation.mutate({ id: editing === 'new' ? null : editing.id, input })
            }
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {bannersQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="배너 목록 불러오는 중" />
        </div>
      ) : bannersQuery.isError ? (
        <EmptyState
          title="배너 목록을 불러오지 못했습니다"
          description="잠시 후 다시 시도해 주세요"
          action={
            <Button variant="tertiary" size="sm" onClick={() => void bannersQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : banners.length === 0 ? (
        <EmptyState
          icon={<GalleryHorizontalEnd size={40} strokeWidth={1.5} />}
          title="배너가 없습니다"
          description="배너 등록 버튼으로 첫 배너를 만들어 보세요"
        />
      ) : (
        <Table>
          <Thead>
            <tr>
              <Th className="w-28">이미지</Th>
              <Th>제목</Th>
              <Th className="w-20">링크</Th>
              <Th className="w-64">기간</Th>
              <Th className="w-16 text-right">정렬</Th>
              <Th className="w-20">상태</Th>
              <Th className="w-32 text-right">
                <span className="sr-only">액션</span>
              </Th>
            </tr>
          </Thead>
          <tbody>
            {banners.map((banner) => {
              const imageKo = jsonLocaleText(banner.image_i18n, 'ko');
              const period =
                banner.starts_at == null && banner.ends_at == null
                  ? '상시'
                  : `${formatDateTime(banner.starts_at) || '즉시'} ~ ${formatDateTime(banner.ends_at) || '상시'}`;
              return (
                <Tr key={banner.id}>
                  <Td>
                    {imageKo !== '' ? (
                      <img
                        src={imagePublicUrl(imageKo)}
                        alt=""
                        className="h-10 w-20 rounded-md border border-line object-cover"
                      />
                    ) : (
                      <div className="h-10 w-20 rounded-md border border-line bg-subtle" />
                    )}
                  </Td>
                  <Td className="font-medium">{banner.title}</Td>
                  <Td className="text-fg-secondary">
                    {LINK_TYPE_LABEL[banner.link_type] ?? banner.link_type}
                  </Td>
                  <Td className="whitespace-nowrap text-fg-secondary tabular-nums">{period}</Td>
                  <Td className="text-right tabular-nums">{banner.sort_order}</Td>
                  <Td>
                    {banner.is_active ? (
                      <Badge tone="success">활성</Badge>
                    ) : (
                      <Badge tone="neutral">비활성</Badge>
                    )}
                  </Td>
                  <Td className="text-right">
                    <Can perm="curation:write">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setEditing(banner)}
                          className="inline-flex h-8 items-center rounded-md bg-subtle px-2.5 font-medium text-fg transition-colors hover:bg-neutral-200"
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(banner)}
                          className="inline-flex h-8 items-center rounded-md px-2.5 font-medium text-danger-600 transition-colors hover:bg-danger-50"
                        >
                          삭제
                        </button>
                      </div>
                    </Can>
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Dialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="배너 삭제"
        description={
          deleteTarget != null ? `'${deleteTarget.title}' 배너를 삭제하시겠습니까?` : undefined
        }
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={() => setDeleteTarget(null)}>
              취소
            </Button>
            <Button
              variant="danger"
              fullWidth
              loading={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget != null) deleteMutation.mutate(deleteTarget.id);
              }}
            >
              삭제
            </Button>
          </>
        }
      />
    </>
  );
}
